//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useEffect } from 'react';
//  Widgets
import { OverlayTrigger, Tooltip, Modal, Button, Card } from 'react-bootstrap';
//  Context
import { useLanguage } from '../../../../../../API/Context/LanguageContext';
import { useUserPrescriptions } from '../../../../../../API/Hooks/useUserPrescriptions';
//  Config
import VocabTreatments from '../../../../../../ConfigFiles/multilingual/VocabTreatments';
import {VocabButtons as btn_vocab} from '../../../../../../ConfigFiles/multilingual/VocabButtons';
import './updateTreatmentModal.css';
//  ----------      END   IMPORT  BLOCK       ----------


const UpdateTreatmentModal = ({ showModal, toggleModal }) => {
  //  Consume context
    const { lan } = useLanguage()
    // NOTE: Assuming updateUserPrescriptions is the correct function for updating instructions status
    const { active_prescriptions, updateUserPrescriptions } = useUserPrescriptions() 

  
  // 3. Initialize state with data from the hook (use empty array as fallback)
  const [prescriptions, setPrescription] = useState(active_prescriptions ?? []);
  const [changesMade, setChangesMade] = useState(false);
  const activeStatus = 1;
  const abortedStatus = 3;

  useEffect(() => {
    // 4. Sync local state with the hook data whenever active_prescriptions changes
    // This deep comparison handles scenarios where the array reference changes but content might be the same.
    if (active_prescriptions && active_prescriptions !== prescriptions) {
      setPrescription(active_prescriptions);
      // When the source data updates (e.g., after a successful save), reset changesMade
      setChangesMade(false); 
    }
  }, [active_prescriptions]); // Dependency array now uses the hook value

  // --- UPDATED: Uses instructionId instead of instructionNumber ---
  const handleDeactivate = (prescriptionId, instructionId) => {
    //  Map over all prescriptions
    const updatedPrescriptions = prescriptions.map((item) => {
      // 1. Check if this is the correct parent prescription
      if (item.id === prescriptionId) {
        // 2. If it is the correct prescription, update its instructions array
        return {
          ...item,
          instructions: item.instructions.map((inst) =>
            // 3. Find the specific instruction by its ID
            inst.id === instructionId 
              ? { ...inst, status: abortedStatus } // Deactivate only the matching instruction
              : inst // Keep other instructions unchanged
          )
        };
      }
      // 4. If it's not the correct prescription, return it unchanged
      return item;
    });
    
    setPrescription(updatedPrescriptions);
    setChangesMade(true);
  };
  // -------------------------------------------------------------

  const handleSave = async () => {
    if (!changesMade) return;
    
    // --- UPDATED: Uses subItem.id for the instruction ID in the payload ---
    const prescriptions_to_update = prescriptions.map(item => ({
      // The prescription ID
      id: item.id, 
      update_instruction_params: item.instructions.map(subItem => ({
        // The instruction ID from the state object
        id: subItem.id, 
        status: subItem.status
      }))
    }));
    // --------------------------------------------------------------------
    
    // Call the update function from the hook
    // NOTE: Using updateUserPrescriptions based on the updated import from the user's provided code.
    await updateUserPrescriptions(prescriptions_to_update);
    
    // Optionally close the modal upon successful save
    toggleModal();
  };

  return (
    <Modal show={showModal} onHide={toggleModal}>
      <Modal.Header closeButton>
        <Modal.Title>{VocabTreatments.active_treatments[lan]}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          {prescriptions
            .filter(prescription => prescription.instructions.some(inst => inst.status === activeStatus))
            .map((prescription, index) => (
              <Card key={`${prescription.id}-${index}`}>
                <Card.Header>
                  <strong>{VocabTreatments.prescription_created[lan]}</strong> {new Date(prescription.creation_date).toLocaleDateString()}
                </Card.Header>
                <Card.Body>
                  {prescription.instructions
                    .filter(inst => inst.status === activeStatus)
                    .map((instruction) => (
                      <div
                        // --- UPDATED: Using instruction.id for key ---
                        key={`${instruction.id}`} 
                        className="d-flex justify-content-between align-items-center mb-2"
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            // --- UPDATED: Using instruction.id for Tooltip id ---
                            <Tooltip id={`tooltip-${instruction.id}`} className="custom-tooltip"> 
                              <div>
                                {instruction.start_date && new Date(instruction.start_date).getFullYear() > 1900 && (
                                  <div>
                                    <strong>{VocabTreatments.instruction_start_date[lan]}:</strong> 
                                    {new Date(instruction.start_date).toLocaleDateString()}
                                  </div>
                                )}
                                {instruction.to_deactivate && new Date(instruction.to_deactivate).getFullYear() > 1900 && (
                                  <div>
                                    <strong>{VocabTreatments.instruction_expexted_end[lan]}:</strong> 
                                    {new Date(instruction.to_deactivate).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                          }
                        >
                          <span className="instruction-text">{instruction.instruction}</span>
                        </OverlayTrigger>

                        <Button
                          variant="danger"
                          // --- UPDATED: Pass instruction.id to handleDeactivate ---
                          onClick={() => handleDeactivate(prescription.id, instruction.id)}
                        >
                          {VocabTreatments.suspend_treatment[lan]}
                        </Button>
                      </div>
                    ))}
                </Card.Body>
              </Card>
            ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggleModal}>
          {btn_vocab.close[lan]}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!changesMade} >
          {btn_vocab.save[lan]}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateTreatmentModal;